/**
 * Date PARSING plus the two locale-format primitives built directly on it. Browser-compatible -- no Node.js dependencies.
 *
 * Homed in const/ rather than beside the date helpers in utils/, because the consumers span all four layers: the shaped
 * formatter leaves (`const/date-format.ts`, `const/date-format-eastern.ts`), pure `utils/` leaves that may not
 * value-import a utils sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist), `tools/` leaves that may not
 * import a tools sibling (`QUAL-NO-CROSS-TOOL-IMPORTS-001`), flow leaves and every frontend surface. Same
 * const-leaf-pinned shape as `const/currency-format.ts`.
 */

import { DEFAULT_LOCALE } from '@federa/const/assistant';
import { MISSING_VALUE_PLACEHOLDER } from '@federa/const/sentinels';
import { normalizeSqliteUtcTimestamp } from '@federa/const/sqlite-timestamp';
import { EPOCH_MS_THRESHOLD, EPOCH_MS_UPPER_BOUND, TIME_UNITS, isPlausibleEpochMs } from '@federa/const/time-units';
import { IS_NUMERIC } from '@federa/const/validation-patterns';
import type { DateInput } from '@federa/types/formatters-date-input';

/**
 * A numeric STRING read as epoch MILLISECONDS when it clears {@link EPOCH_MS_THRESHOLD}, else null -- the THRESHOLD-ONLY
 * read `formatSafeWith` (`const/date-format.ts`) and the D1 sort-key parser (`utils/dates/epoch-ms.ts`) each spelled.
 * Deliberately NOT the bounded `isPlausibleEpochMs` bracket: the recorded gap on that export is exactly that these two
 * readers carry no upper bound, and adopting one would change what they accept.
 * @runtime-derived-ssot-helper (const-leaf-pinned): a `utils/` parser reads it from the const floor.
 */
export function numericEpochMsOrNull(value: string): number | null {
	const numeric = Number(value);
	return !Number.isNaN(numeric) && numeric > EPOCH_MS_THRESHOLD ? numeric : null;
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): the ONE date-coercion decision every formatter below reads. Null when invalid. */
export function parseDate(value: DateInput): Date | null {
	if (value === null || value === undefined) return null;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

	// Handle string numeric timestamps: JavaScript's Date constructor treats numeric strings as date strings, not timestamps.
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (IS_NUMERIC.test(trimmed)) {
			const numeric = Number.parseInt(trimmed, 10);
			// Bounds are year 2001 to year 2100 in milliseconds -- the bracket is `isPlausibleEpochMs`, not a comparison
			// pair re-assembled here (`const/time-units.ts` owns it).
			if (isPlausibleEpochMs(numeric)) {
				return new Date(numeric);
			}
			// Same range read as SECONDS.
			if (numeric > EPOCH_MS_THRESHOLD / TIME_UNITS.SECOND_MS && numeric < EPOCH_MS_UPPER_BOUND / TIME_UNITS.SECOND_MS) {
				return new Date(numeric * TIME_UNITS.SECOND_MS);
			}
		}
	}

	// The zone-less D1 wall-clock shape is stamped UTC here, so every downstream `formatLocale*` / `formatDate*` reader
	// inherits the fix from this one call rather than each re-deciding it.
	const date = new Date(normalizeSqliteUtcTimestamp(value));
	return Number.isNaN(date.getTime()) ? null : date;
}

function formatLocaleImpl(
	opts: {
		value: DateInput;
		options: Intl.DateTimeFormatOptions;
		sentinel: string;
	},
	method: 'toLocaleDateString' | 'toLocaleTimeString',
): string {
	const date = parseDate(opts.value);
	if (!date) return opts.sentinel;
	return date[method](DEFAULT_LOCALE, opts.options);
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): parse-then-`toLocaleDateString` under `DEFAULT_LOCALE`. Returns `sentinel` (default `MISSING_VALUE_PLACEHOLDER`) for an invalid date. */
export function formatLocaleDate(value: DateInput, options: Intl.DateTimeFormatOptions, sentinel: string = MISSING_VALUE_PLACEHOLDER): string {
	return formatLocaleImpl({ value, options, sentinel }, 'toLocaleDateString');
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): parse-then-`toLocaleTimeString` under `DEFAULT_LOCALE`. Returns `sentinel` (default `MISSING_VALUE_PLACEHOLDER`) for an invalid date. */
export function formatLocaleTime(value: DateInput, options: Intl.DateTimeFormatOptions, sentinel: string = MISSING_VALUE_PLACEHOLDER): string {
	return formatLocaleImpl({ value, options, sentinel }, 'toLocaleTimeString');
}
